import cv from "@techstark/opencv-js";
export interface GridBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface BinaryImage {
  width: number;
  height: number;
  data: Uint8Array;
}

const MAX_WIDTH = 1800;

const BLACK = 1;
const WHITE = 0;

function toGray(
  r: number,
  g: number,
  b: number
): number {
  return 0.299 * r +
         0.587 * g +
         0.114 * b;
}

function otsuThreshold(
  gray: Uint8Array
): number {

  const hist = new Array(256).fill(0);

  for (const p of gray)
    hist[p]++;

  const total = gray.length;

  let sum = 0;

  for (let i = 0; i < 256; i++)
    sum += i * hist[i];

  let sumB = 0;
  let wB = 0;
  let maximum = 0;
  let threshold = 128;

  for (let t = 0; t < 256; t++) {

    wB += hist[t];

    if (!wB)
      continue;

    const wF = total - wB;

    if (!wF)
      break;

    sumB += t * hist[t];

    const mB = sumB / wB;
    const mF = (sum - sumB) / wF;

    const variance =
      wB *
      wF *
      (mB - mF) *
      (mB - mF);

    if (variance > maximum) {
      maximum = variance;
      threshold = t;
    }

  }

  return threshold;

}
async function loadBinaryImage(
  file: File
): Promise<BinaryImage> {

  const img = new Image();
  const url = URL.createObjectURL(file);

  return new Promise((resolve, reject) => {

    img.onload = () => {

      const scale =
        img.width > MAX_WIDTH
          ? MAX_WIDTH / img.width
          : 1;

      const canvas =
        document.createElement("canvas");

      canvas.width =
        Math.round(img.width * scale);

      canvas.height =
        Math.round(img.height * scale);

      const ctx =
        canvas.getContext("2d");

      if (!ctx) {
        reject(
          new Error("Canvas unavailable")
        );
        return;
      }

      ctx.drawImage(
        img,
        0,
        0,
        canvas.width,
        canvas.height
      );

      const image =
        ctx.getImageData(
          0,
          0,
          canvas.width,
          canvas.height
        );

      const gray =
        new Uint8Array(
          canvas.width *
          canvas.height
        );

      for (
        let i = 0, j = 0;
        i < image.data.length;
        i += 4, j++
      ) {

        gray[j] = toGray(
          image.data[i],
          image.data[i + 1],
          image.data[i + 2]
        );

      }

      const threshold =
        otsuThreshold(gray);

      const binary =
        new Uint8Array(gray.length);

      for (
        let i = 0;
        i < gray.length;
        i++
      ) {

        binary[i] =
          gray[i] < threshold
            ? BLACK
            : WHITE;

      }

      URL.revokeObjectURL(url);

      resolve({

        width: canvas.width,
        height: canvas.height,
        data: binary

      });

    };

    img.onerror = () => {

      URL.revokeObjectURL(url);

      reject(
        new Error(
          "Unable to load image"
        )
      );

    };

    img.src = url;

  });

}
function mergeNearbyLines(
  values: number[],
  gap = 6
): number[] {

  if (values.length === 0)
    return [];

  values.sort((a, b) => a - b);

  const merged: number[] = [];

  let start = values[0];
  let end = values[0];

  for (let i = 1; i < values.length; i++) {

    if (values[i] - end <= gap) {
      end = values[i];
      continue;
    }

    merged.push(
      Math.round((start + end) / 2)
    );

    start = values[i];
    end = values[i];

  }

  merged.push(
    Math.round((start + end) / 2)
  );

  return merged;

}

function detectHorizontalLines(
  image: BinaryImage
): number[] {

  const rows: number[] = [];

  for (let y = 0; y < image.height; y++) {

    let run = 0;
    let longest = 0;

    for (let x = 0; x < image.width; x++) {

      if (
        image.data[
          y * image.width + x
        ] === BLACK
      ) {

        run++;

        if (run > longest)
          longest = run;

      } else {

        run = 0;

      }

    }

if (y % 50 === 0) {
  console.log(
    "Row",
    y,
    "Longest run",
    longest
  );
}
    if (
      longest >
      image.width * 0.65
    ) {

      rows.push(y);

    }

  }

  return mergeNearbyLines(
    rows,
    5
  );

}

function detectVerticalLines(
  image: BinaryImage
): number[] {

  const cols: number[] = [];

  for (let x = 0; x < image.width; x++) {

    let run = 0;
    let longest = 0;

    for (let y = 0; y < image.height; y++) {

      if (
        image.data[
          y * image.width + x
        ] === BLACK
      ) {

        run++;

        if (run > longest)
          longest = run;

      } else {

        run = 0;

      }

    }
console.log(
  "Column",
  x,
  "Longest run",
  longest
);
if (x % 50 === 0) {
  console.log(
    "Column",
    x,
    "Longest run",
    longest
  );
} 
   if (
      longest >
      image.height * 0.65
    ) {

      cols.push(x);

    }

  }

  return mergeNearbyLines(
    cols,
    5
  );

}
export async function detectTableGrid(
  file: File
): Promise<GridBox[]> {

  console.log("ENTER detectTableGrid");

  const image =
    await loadBinaryImage(file);

  let horizontal =
    detectHorizontalLines(image);

  let vertical =
    detectVerticalLines(image);
  // Ignore page borders
  horizontal = horizontal.filter(
    y =>
      y > 15 &&
      y < image.height - 15
  );

  vertical = vertical.filter(
    x =>
      x > 15 &&
      x < image.width - 15
  );

  console.log(
    "Horizontal lines:",
    horizontal.length,
    horizontal
  );

  console.log(
    "Vertical lines:",
    vertical.length,
    vertical
  );

  const boxes: GridBox[] = [];

  for (
    let r = 0;
    r < horizontal.length - 1;
    r++
  ) {

    for (
      let c = 0;
      c < vertical.length - 1;
      c++
    ) {

      const x = vertical[c];
      const y = horizontal[r];

      const width =
        vertical[c + 1] - x;

      const height =
        horizontal[r + 1] - y;

// Reject unusable cells
if (
  width < 40 ||
  height < 25
) {
  continue;
}

// Reject very thin cells
const aspect = width / height;

if (
  aspect < 0.35 ||
  aspect > 12
) {
  continue;
}

// Ignore tiny OCR regions
const area = width * height;

if (
  area < 2500
) {
  continue;
}
      boxes.push({
        x,
        y,
        width,
        height
      });

    }

  }

  console.log(
    "Detected cells:",
    boxes.length
  );

  if (boxes.length) {

    let minW = Number.MAX_VALUE;
    let minH = Number.MAX_VALUE;

    for (const b of boxes) {

      if (b.width < minW)
        minW = b.width;

      if (b.height < minH)
        minH = b.height;

    }

    console.log(
      "Smallest cell:",
      minW,
      "x",
      minH
    );

  }
console.table(
  boxes.slice(0, 20)
);
  return boxes;

}
